/**
 * CalciumPthCard — iter1053
 * Калций (кръв и 24ч урина), PTH, витамин D, фосфор и бикарбонат —
 * прочетени като КОМБИНАЦИЯ. Един източник, показан и в бъбречния, и в
 * хормоналния близнак.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Loader2, Bone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CalciumPthCard = ({ isDark }) => {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/calcium-pth/snapshot`, { timeout: 25000 })
      .then(({ data }) => setD(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className={`p-4 rounded-2xl border ${isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}`}
                data-testid="calcium-pth-loading">
      <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
    </div>;
  }
  if (!d) return null;
  const p = d.pattern || {};
  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const box = isDark ? "bg-slate-800/40" : "bg-slate-50 border border-slate-200";

  return (
    <div className={`p-4 rounded-2xl border ${card}`} data-testid="calcium-pth-card">
      <div className="flex items-center gap-2 mb-1">
        <Bone className="w-4 h-4 text-amber-500" />
        <h2 className="font-bold text-sm">{d.title_bg}</h2>
        <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap ${
          p.key === "no_data" ? "bg-slate-400 text-white"
          : p.urgency === "high" ? "bg-rose-500 text-white"
          : p.key === "balanced" ? "bg-emerald-500 text-white"
          : "bg-amber-500 text-white"
        }`} data-testid="calcium-pattern-status">
          {p.key === "no_data" ? "Няма данни" : p.key === "balanced" ? "В равновесие" : "За четене"}
        </span>
      </div>
      <div className="text-[11px] font-semibold" data-testid="calcium-pattern-label">{p.label_bg}</div>
      <p className="text-[11px] leading-relaxed opacity-90 mt-1" data-testid="calcium-pattern-text">{p.text_bg}</p>
      {p.next_bg && (
        <div className={`mt-2 p-2 rounded-lg text-[10px] ${isDark ? "bg-amber-500/10" : "bg-amber-50"}`}
             data-testid="calcium-next">
          <strong>Следваща стъпка:</strong> {p.next_bg}
        </div>
      )}

      {(d.values || []).length > 0 && (
        <div className={`mt-2 p-2 rounded-lg text-[10px] ${box}`} data-testid="calcium-values">
          {d.values.map((v) => (
            <div key={v.label_bg} className="flex gap-2">
              <span className="flex-1 min-w-0">{v.label_bg}</span>
              <strong className="tabular-nums">{v.value}{v.unit ? ` ${v.unit}` : ""}</strong>
              <span className="opacity-60">({v.ref_bg})</span>
            </div>
          ))}
        </div>
      )}

      <button onClick={() => setOpen((v) => !v)} data-testid="calcium-toggle"
              className={`mt-2 w-full text-[11px] font-semibold py-2 rounded-lg transition-colors ${
                isDark ? "bg-amber-500/15 text-amber-200 hover:bg-amber-500/25"
                       : "bg-amber-50 text-amber-700 hover:bg-amber-100"
              }`}>
        {open ? "Скрий подробностите" : "Механизъм, лостове и какво липсва"}
      </button>

      {open && (
        <div className="mt-2 space-y-2" data-testid="calcium-details">
          <p className="text-[10px] italic opacity-75">{d.mechanism_bg}</p>
          {(d.levers || []).length > 0 && (
            <div className={`p-2 rounded-lg text-[10px] ${isDark ? "bg-emerald-500/10" : "bg-emerald-50 border border-emerald-200"}`}
                 data-testid="calcium-levers">
              <div className="font-bold opacity-80 mb-1">Лостове с числа</div>
              {d.levers.map((l) => (
                <div key={l.title_bg} className="mb-1.5">
                  <strong>{l.title_bg}</strong> — <span className="font-semibold">{l.target_bg}</span>
                  <div className="opacity-80">{l.why_bg}</div>
                </div>
              ))}
            </div>
          )}
          {(d.missing || []).length > 0 && (
            <div className={`p-2 rounded-lg text-[10px] ${box}`} data-testid="calcium-missing">
              <div className="font-bold opacity-70 mb-1">Какво липсва, за да се реши</div>
              {d.missing.map((m) => (
                <div key={m.marker} className="mb-0.5"><strong>{m.label_bg}</strong> — {m.why_bg}</div>
              ))}
            </div>
          )}
          {(d.links || []).map((l, i) => (
            <button key={l.route} onClick={() => navigate(l.route)}
                    data-testid={`calcium-link-${i}`}
                    className={`w-full text-left p-2 rounded-lg text-[10px] transition-colors ${
                      isDark ? "bg-sky-500/10 hover:bg-sky-500/20" : "bg-sky-50 hover:bg-sky-100"
                    }`}>
              <span className="font-bold">{l.title_bg} →</span>
              <span className="opacity-80"> {l.action_bg}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CalciumPthCard;
